import { useNavigate } from '@tanstack/react-router';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
} from 'react';
import { useAuth } from '../auth/AuthProvider.js';
import { useI18n } from '../i18n/I18nProvider.js';
import { applyTheme, nextTheme, readTheme } from '../theme.js';

/**
 * Command palette.
 *
 * Keyboard-first is a stated goal for the desktop experience (plan §11), and a palette is the
 * cheapest way to make every destination reachable without learning a menu. Opened with
 * Ctrl/Cmd-K, closed with Escape, navigated with the arrow keys.
 *
 * Accessibility: a native modal `<dialog>` handles focus trapping and Escape. The list is a real
 * listbox with `aria-activedescendant`, so a screen reader announces the highlighted item as the
 * arrow keys move — the highlight is not conveyed by background colour alone.
 */

interface Command {
  readonly id: string;
  readonly label: string;
  readonly section: 'navigate' | 'actions';
  readonly run: () => void | Promise<void>;
}

const NAVIGATION: readonly { path: string; key: string }[] = [
  { path: '/', key: 'nav.today' },
  { path: '/decisions', key: 'nav.decisions' },
  { path: '/rescue', key: 'nav.rescue' },
  { path: '/settings/mailboxes', key: 'nav.mailboxes' },
  { path: '/settings/autonomy', key: 'nav.autonomy' },
  { path: '/settings/security', key: 'security.title' },
];

export function CommandPalette(): ReactNode {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlighted, setHighlighted] = useState(0);
  const dialogRef = useRef<HTMLDialogElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const commands = useMemo<Command[]>(
    () => [
      ...NAVIGATION.map((entry) => ({
        id: `navigate:${entry.path}`,
        label: t(entry.key),
        section: 'navigate' as const,
        run: () => navigate({ to: entry.path }),
      })),
      {
        id: 'action:theme',
        label: t('command.action.toggleTheme'),
        section: 'actions',
        run: () => applyTheme(nextTheme(readTheme())),
      },
      {
        id: 'action:sign-out',
        label: t('command.action.signOut'),
        section: 'actions',
        run: signOut,
      },
    ],
    [t, navigate, signOut],
  );

  const matches = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (needle === '') return commands;
    return commands.filter((command) => command.label.toLowerCase().includes(needle));
  }, [commands, query]);

  const close = useCallback(() => {
    setOpen(false);
    setQuery('');
    setHighlighted(0);
  }, []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setOpen((current) => !current);
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (dialog === null) return;

    if (open && !dialog.open) {
      dialog.showModal();
      inputRef.current?.focus();
    } else if (!open && dialog.open) {
      dialog.close();
    }
  }, [open]);

  const runHighlighted = useCallback(() => {
    const command = matches[highlighted];
    if (command === undefined) return;
    close();
    void command.run();
  }, [matches, highlighted, close]);

  const onKeyDown = (event: ReactKeyboardEvent) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setHighlighted((current) => (current + 1) % Math.max(matches.length, 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setHighlighted((current) => (current - 1 + matches.length) % Math.max(matches.length, 1));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      runHighlighted();
    }
  };

  const sections: { section: Command['section']; label: string }[] = [
    { section: 'navigate', label: t('command.section.navigate') },
    { section: 'actions', label: t('command.section.actions') },
  ];

  return (
    <dialog ref={dialogRef} className="palette" onClose={close} aria-label={t('command.open')}>
      <div className="palette__body">
        <input
          ref={inputRef}
          className="palette__input"
          type="text"
          role="combobox"
          aria-expanded="true"
          aria-controls="palette-list"
          {...(matches[highlighted] === undefined
            ? {}
            : { 'aria-activedescendant': matches[highlighted].id })}
          placeholder={t('command.placeholder')}
          value={query}
          onChange={(event) => {
            setQuery(event.target.value);
            setHighlighted(0);
          }}
          onKeyDown={onKeyDown}
        />

        <ul
          className="palette__list"
          id="palette-list"
          role="listbox"
          aria-label={t('command.open')}
        >
          {matches.length === 0 ? <li className="palette__empty">{t('command.empty')}</li> : null}

          {sections.map(({ section, label }) => {
            const items = matches.filter((command) => command.section === section);
            if (items.length === 0) return null;

            return (
              <li key={section}>
                <p className="palette__section">{label}</p>
                <ul role="group" aria-label={label}>
                  {items.map((command) => {
                    const index = matches.indexOf(command);
                    return (
                      <li
                        key={command.id}
                        id={command.id}
                        role="option"
                        aria-selected={index === highlighted}
                        className={`palette__item${
                          index === highlighted ? ' palette__item--active' : ''
                        }`}
                        onMouseEnter={() => setHighlighted(index)}
                        onClick={runHighlighted}
                      >
                        {command.label}
                      </li>
                    );
                  })}
                </ul>
              </li>
            );
          })}
        </ul>
      </div>
    </dialog>
  );
}
